import React, { useEffect, useState } from 'react';
import { sortBy } from "lodash";

import Grid from "@mui/material/Grid2";
import { useTheme } from "@mui/material/styles";

import { useInsightFilters } from "@/context";
import {
  Audience,
  AudienceComparison,
  CompareInsight,
  LocationResult,
  LocationScore,
  PenPortraitCompareInsight,
  PenPortraitInsight,
  PenPortraitInsightVariable,
  PenPortraitSection,
} from "@/types";
import { allHaveKey, compareStat, comparisonGradient, getSegmentPaletteColor } from "@/utils";
import { CompareInsightView } from "@/components/molecules";


interface CompareViewProps {
  audiences: Audience[];
  insights: CompareInsight[];
  mode: string | null;
}

const CompareView: React.FC<CompareViewProps> = ({
  audiences,
  insights,
  mode
}) => {
  const {
    filters: { scoreType },
  } = useInsightFilters();

  const theme = useTheme();
  const [comparison, setCompare] = useState<AudienceComparison | undefined>();
  const [sections, setSections] = useState<PenPortraitSection[]>([]);

  useEffect(() => {

    if (insights.length > 0) {
      const firstInsight = insights[0].insight
      if (firstInsight) {
        if (firstInsight.mindsets) {
          setSections([
            { title: "mindset", id: 99, score: 0 },
            ...firstInsight.pen_portraits[0].sections,
          ])
        } else {
          setSections(firstInsight.pen_portraits[0].sections)
        }
      }
    }
  }, [insights]);

  const buildCompare = () => {
    // Check that two audiences and two insights are available
    if (
      audiences.length === 2 &&
      insights.length === 2 &&
      allHaveKey(insights, "insight")
    ) {

      // Get a comparison of audience sizes
      const audienceCompare = compareAudiences(
        audiences[0],
        audiences[1]
      )

      // Get a comparison of the audience insight
      const insightCompare = compareInsights(
        insights[0].insight.pen_portraits[0].insight,
        insights[1].insight.pen_portraits[0].insight,
        sections
      )

      // Get a comparison of the mindset insight
      const mindsetCompare = compareMindsets(
        insights[0].insight.mindsets[0],
        insights[1].insight.mindsets[0]
      )

      // Get a comparison of the location scores
      const locationCompare = compareLocations(
        insights[0].insight.locations,
        insights[1].insight.locations
      )


      return {
        audience_size: audienceCompare,
        pen_portrait: insightCompare,
        mindsets: mindsetCompare,
        locations: locationCompare
      }
    }
  }

  const compareAudiences = (audienceA: Audience, audienceB: Audience) => {

    if (audienceA.audience_size && audienceB.audience_size) {

      const summaryA = audienceA.audience_size[0]
      const summaryB = audienceB.audience_size[0]

      return {
        num_postcodes: summaryA.num_postcodes && summaryB.num_postcodes &&
          compareStat(summaryA.num_postcodes, summaryB.num_postcodes),
        num_households: compareStat(
          summaryA.num_households, summaryB.num_households
        ),
        population_count: compareStat(
          summaryA.population_count, summaryB.population_count
        ),
      }
    } else {
      return {
        num_postcodes: 0,
        num_households: 0,
        population_count: 0
      }
    }
  };

  const compareInsights = (
    insightsA: PenPortraitInsight[],
    insightsB: PenPortraitInsight[],
    sections: PenPortraitSection[]): PenPortraitCompareInsight[] => {


    return sections.map((section, idx) => {
      const varA: PenPortraitInsight | undefined = insightsA.find((v) => v.section === section.title);
      const varB: PenPortraitInsight | undefined = insightsB.find((v) => v.section === section.title);

      if (varA && varB) {

        const tagsA: PenPortraitInsightVariable[] = sortBy(varA.variables, scoreType.value)
          .reverse()
          .slice(0, 6);

        const tagsB: PenPortraitInsightVariable[] = sortBy(varB.variables, scoreType.value)
          .reverse()
          .slice(0, 6);

        const compScore = compareStat(
          tagsA.reduce((sum, { score }) => sum + score, 0) / 6,
          tagsB.reduce((sum, { score }) => sum + score, 0) / 6
        );
        const idsA = new Set(tagsA.map(x => x.tag_id));

        const overlapping = tagsB.filter(item => {
          switch(mode) {
            case "Diff":
              return idsA.has(item.tag_id)
            case "Overlap":
              return !idsA.has(item.tag_id)
            default:
              return false
          }
        }).map(item => item.tag_id);

        return {
          section,
          score_diff: compScore,
          overlapping
        }

      } else {
        return {
          section,
          score_diff: 0,
          overlapping: []
        }
      }
    })
  };

  const compareMindsets = (
    mindsetsA: PenPortraitInsightVariable[],
    mindsetsB: PenPortraitInsightVariable[]
  ) => {
    const tagsA = new Set(sortBy(mindsetsA, scoreType.value)
      .reverse()
      .slice(0, 6)
      .map(item => item.tag_name));
    const tagsB = sortBy(mindsetsB, scoreType.value)
      .reverse()
      .slice(0, 6)

    switch(mode) {
      case "Diff":
        return tagsB.filter(item => tagsA.has(item.tag_name)).map(item => item.tag_id)
      case "Overlap":
        return tagsB.filter(item => !tagsA.has(item.tag_name)).map(item => item.tag_id)
      default:
        return []
    }
  };

  const compareLocations = (
    locationsA: LocationResult[],
    locationsB: LocationResult[]
  ) => {

    const areasA = locationsA.flatMap(l => l.area);
    const areasB = locationsB.flatMap(l => l.area);

    const idsA = new Set(areasA.map(a => a.gid));
    const idsB = new Set(areasB.map(a => a.gid));

    const mapB = new Map<number, LocationScore>(
      areasB.map(a => [a.gid, a])
    );

    switch(mode) {
      case "Diff":

        const aLowerThanB: LocationScore[] = [];
        const bLowerThanA: LocationScore[] = [];

        areasA.forEach(areaA => {
          const areaB = mapB.get(areaA.gid);
          if (areaB) {
            if (areaA.rate < areaB.rate) {
              aLowerThanB.push(areaA);
            } else if (areaB.rate < areaA.rate) {
              bLowerThanA.push(areaB);
            }

          }}
        )

        return [aLowerThanB, bLowerThanA]

      case "Overlap":

        return [
          areasA.filter(a => !idsB.has(a.gid)),
          areasB.filter(a => !idsA.has(a.gid))
        ];

      default:
        return [[], []]
    }
  }

  useEffect(() => {
    setCompare(buildCompare);
  }, [audiences, insights, scoreType.value, mode])

  return (
    <Grid size={12} container spacing={2}>
      {insights.map((item, idx) => (
        <CompareInsightView
          key={idx}
          index={idx}
          audience={audiences[idx]}
          insight={item.insight}
          score={scoreType.value}
          sections={sections}
          comparison={comparison}
          loading={item.loading}
          color={getSegmentPaletteColor(theme, idx, 2)}
          gradient={idx !== 0 ? comparisonGradient : undefined}
        />
      ))}
    </Grid>
  );
};

export default CompareView;
